import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import type {
  ChatMessage,
  ChatSessionWithMessagesResponse,
} from '@karehero/models';

import { useDispatch, useSelector } from 'store';
import {
  chatSessionApi,
  useCreateChatSessionMutation,
  useListChatSessionsQuery,
  useUpdateChatSessionMutation,
} from 'store/api/chatSession';
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { getOnboardingV3 } from 'store/slices/onboardingV3Slice';
import { newToast } from 'store/slices/uiSlice';
import {
  isOnboardingV4FlowID,
  onboardingV4Flow,
  type ConsentStyle,
  type OnboardingV4FlowID,
} from 'config/onboardingV4/flows';
import {
  newProgress,
  progressFrom,
  savedProgress,
  type OnboardingV4Progress,
  type OnboardingV4Stage,
} from 'utils/onboardingV4Progress';
import { toChatMessage } from 'utils/chatMessages';
import { useChatSessionWriter } from './useChatSessionWriter';
import { useMessageOutbox } from './useMessageOutbox';
import { useProcessingDocuments } from './useProcessingDocuments';
import { TrackingEvent, useTracking } from './useTracking';

const CHAT_SESSION_MODE = 'onboarding';

const savedFlowID = (metadata?: Record<string, unknown>) =>
  savedProgress(metadata)?.flowID;

/** The latest session a v4 flow began; a v3 session is left alone. */
export const latestV4Session = (
  sessions: readonly ChatSessionWithMessagesResponse[],
) =>
  sessions.find(({ session }) =>
    isOnboardingV4FlowID(savedFlowID(session.metadata)),
  );

export interface UseOnboardingV4SessionOptions {
  /**
   * The flow a new session starts, or undefined while the flag loads; a begun
   * session keeps its own.
   */
  flowID?: OnboardingV4FlowID;
  /** Where a new session asks consent to AI; a begun session keeps its own. */
  consentStyle?: ConsentStyle;
}

/**
 * useOnboardingV4Session starts or resumes the chat session onboarding v4 is
 * kept in, with its messages and progress.
 */
export const useOnboardingV4Session = ({
  flowID,
  consentStyle,
}: UseOnboardingV4SessionOptions) => {
  const dispatch = useDispatch();
  const { track } = useTracking();
  const careCircleId = useSelector(getCurrentCareCircle)?.id;
  const { processedDocumentIds = [], processedMessageIds = [] } =
    useSelector(getOnboardingV3);
  const [createChatSession] = useCreateChatSessionMutation();
  const [updateChatSession] = useUpdateChatSessionMutation();

  const queryArgs = useMemo(
    () => ({
      careCircleId: careCircleId ?? '',
      mode: CHAT_SESSION_MODE,
      status: 'active',
    }),
    [careCircleId],
  );
  const { data: sessions, isFetching } = useListChatSessionsQuery(queryArgs, {
    skip: !careCircleId,
  });

  const [sessionId, setSessionId] = useState<string>();
  const [progress, setProgress] = useState<OnboardingV4Progress | null>(null);
  const progressRef = useRef(progress);
  const isSessionResolved = useRef(false);
  // The care circle shown now, so a session started for another is dropped.
  const careCircleIdRef = useRef(careCircleId);

  // Another care circle starts over: its own session, its own progress.
  useEffect(() => {
    careCircleIdRef.current = careCircleId;
    isSessionResolved.current = false;
    progressRef.current = null;
    setSessionId(undefined);
    setProgress(null);
  }, [careCircleId]);

  // Named as v4's before it is used, so it is never mistaken for v3's. One left
  // unnamed is kept rather than started over; the next saved progress names it.
  const nameSession = useCallback(
    async (
      careCircleId: string,
      id: string,
      progress: OnboardingV4Progress,
    ) => {
      try {
        await updateChatSession({
          careCircleId,
          sessionId: id,
          body: { metadata: { onboardingV4: progress } },
        }).unwrap();
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [updateChatSession],
  );

  const startSession = useCallback(
    async (careCircleId: string, progress: OnboardingV4Progress) => {
      try {
        const { id } = await createChatSession({
          careCircleId,
          body: { mode: CHAT_SESSION_MODE },
        }).unwrap();
        await nameSession(careCircleId, id, progress);
        if (careCircleIdRef.current === careCircleId) setSessionId(id);
      } catch (err) {
        Sentry.captureException(err);
        if (careCircleIdRef.current !== careCircleId) return;
        dispatch(
          newToast({
            title: 'Something went wrong',
            description:
              'We could not start your onboarding. Please try again.',
            variant: 'error',
          }),
        );
      }
    },
    [createChatSession, dispatch, nameSession],
  );

  // Picks up the latest v4 session, or starts one once the flag has loaded.
  useEffect(() => {
    if (!careCircleId || !sessions || isFetching) return;
    if (isSessionResolved.current) return;

    const found = latestV4Session(sessions);
    if (found) {
      isSessionResolved.current = true;
      setSessionId(found.session.id);
    } else if (flowID) {
      isSessionResolved.current = true;
      startSession(careCircleId, newProgress(flowID, consentStyle));
    }
  }, [careCircleId, consentStyle, flowID, isFetching, sessions, startSession]);

  const sessionData = sessions?.find(({ session }) => session.id === sessionId);
  const flow = onboardingV4Flow(
    savedFlowID(sessionData?.session.metadata) ?? flowID,
  );

  const apiMessages = useMemo(
    () => sessionData?.messages ?? [],
    [sessionData?.messages],
  );
  const messages = useMemo<ChatMessage[]>(
    () => apiMessages.map((m) => toChatMessage(m, processedDocumentIds)),
    [apiMessages, processedDocumentIds],
  );
  useProcessingDocuments(careCircleId, messages);
  const processed = useMemo(
    () => ({
      documentIDs: processedDocumentIds,
      messageIDs: processedMessageIds,
    }),
    [processedDocumentIds, processedMessageIds],
  );

  const { enqueueWrite, persistMessage, updateMessage } = useChatSessionWriter({
    careCircleId,
    sessionId,
    queryArgs,
  });
  const {
    send: sendMessage,
    resend: resendMessage,
    sentCount,
  } = useMessageOutbox(updateMessage);

  // Read back once from the session; after that it is kept here and saved.
  useEffect(() => {
    if (progress || !sessionData) return;

    const read = progressFrom(sessionData.session.metadata, flow.id);
    progressRef.current = read;
    setProgress(read);
  }, [flow.id, progress, sessionData]);

  const saveProgress = useCallback(
    (change: (current: OnboardingV4Progress) => OnboardingV4Progress) => {
      const current = progressRef.current;
      if (!current || !sessionId || !careCircleId) {
        Sentry.captureException(
          new Error(
            'Onboarding v4 progress changed before its session was ready',
          ),
        );
        return;
      }

      const next = change(current);
      progressRef.current = next;
      setProgress(next);

      // Patched in the cache too, so a remount reads this back.
      const metadata = { onboardingV4: next };
      dispatch(
        chatSessionApi.util.updateQueryData(
          'listChatSessions',
          queryArgs,
          (draft) => {
            const session = draft.find((s) => s.session.id === sessionId);
            if (session) session.session.metadata = metadata;
          },
        ),
      );
      enqueueWrite(() =>
        updateChatSession({ careCircleId, sessionId, body: { metadata } }),
      );
    },
    [
      careCircleId,
      dispatch,
      enqueueWrite,
      queryArgs,
      sessionId,
      updateChatSession,
    ],
  );

  const finishBeat = useCallback(
    (beatID: string, changes: Partial<OnboardingV4Progress> = {}) => {
      saveProgress((p) => ({ ...p, ...changes, done: [...p.done, beatID] }));
      track(TrackingEvent.OnboardingV4BeatFinished, {
        flowID: flow.id,
        beatID,
      });
    },
    [flow.id, saveProgress, track],
  );

  const markAsked = useCallback(
    (...ids: string[]) =>
      saveProgress((p) => ({ ...p, asked: [...p.asked, ...ids] })),
    [saveProgress],
  );

  const setStage = useCallback(
    (stage: OnboardingV4Stage) => saveProgress((p) => ({ ...p, stage })),
    [saveProgress],
  );

  const completeSession = useCallback(() => {
    if (!sessionId || !careCircleId) return;

    enqueueWrite(() =>
      updateChatSession({
        careCircleId,
        sessionId,
        body: { status: 'completed' },
      }),
    );
  }, [careCircleId, enqueueWrite, sessionId, updateChatSession]);

  return {
    careCircleId,
    sessionId,
    flow,
    apiMessages,
    messages,
    processed,
    progress,
    saveProgress,
    finishBeat,
    markAsked,
    setStage,
    completeSession,
    persistMessage,
    updateMessage,
    sendMessage,
    resendMessage,
    sentCount,
  };
};
